/// <reference types="reselect" />
/// <reference types="immer" />

/* eslint-disable unicorn/no-keyword-prefix */
/* eslint-disable unicorn/no-array-for-each */

import { type Dispatch } from 'redux';
import { useSelector } from 'react-redux';
import { UserService } from 'app/services/userService';
import { type IUser, type Requests } from 'app/interfaces/User.interface';
import { type AppState } from 'app/App.store';
import {
	createSelector,
	createSlice,
	type PayloadAction,
} from '@reduxjs/toolkit';

export interface EmpresaActualInterface {
	estaDeshabilitado: boolean;
	fechaCreacion: string;
	fechaModificacion: string;
	nombre: string;
	rutEmpresa: string;
	direccion: string;
}

interface DataCasaMatrizInterface {
	nombre: boolean;
	rut: boolean;
	direccion: boolean;
	telefono: boolean;
	celular: boolean;
	correo: boolean;
}
interface DataLegalInterface {
	nombre: boolean;
	rut: boolean;
	direccion: boolean;
	telefono: boolean;
	celular: boolean;
	correo: boolean;
}

interface LoadingDataInterface {
	empresa: boolean;
	casaMatriz: DataCasaMatrizInterface;
	Legal: DataLegalInterface;
}

export interface UserInterface {
	nombre: string;
	rut: string;
	roles: any[];
}

export interface UserState {
	status: string;
	error: null | undefined;
	enterprise: {
		usersandworkers: Requests[];
		pendingRequests: Requests[];
	};
	employee: {
		myrequests: Requests[];
	};
	optionTabs: string;
	isMenuOpen: boolean;
	empresaActual: EmpresaActualInterface;
	loadingData: LoadingDataInterface;
	switch: string;
	profile: string;
	user: UserInterface;
}

export const initialState: UserState = {
	status: 'idle',
	error: null,
	enterprise: {
		usersandworkers: [],
		pendingRequests: [],
	},
	employee: {
		myrequests: [],
	},
	optionTabs: 'usersandworkers',
	isMenuOpen: false,
	empresaActual: {
		estaDeshabilitado: false,
		fechaCreacion: '',
		fechaModificacion: '',
		nombre: '',
		rutEmpresa: '',
		direccion: '',
	},
	loadingData: {
		empresa: false,
		casaMatriz: {
			nombre: false,
			rut: false,
			direccion: false,
			telefono: false,
			celular: false,
			correo: false,
		},
		Legal: {
			nombre: false,
			rut: false,
			direccion: false,
			telefono: false,
			celular: false,
			correo: false,
		},
	},
	switch:
		localStorage.getItem('profile') === 'Enterprise'
			? 'Enterprise'
			: 'Employee',
	user: {
		nombre: '',
		rut: '',
		roles: [],
	},
	profile:
		localStorage.getItem('profile') === 'Enterprise'
			? 'enterprise'
			: 'employee',
};

export const userSlice = createSlice({
	name: 'user',
	initialState,
	reducers: {
		userLoading(state) {
			state.status = 'loading';
		},
		userLoaded(state, action: PayloadAction<IUser>) {
			const newRequests: Requests[] = [];
			const newPendingRequests: Requests[] = [];
			const newMyRequests: Requests[] = [];

			action.payload.enterprise.usersandworkers.forEach(
				(request: Requests) => {
					newRequests[request.id] = request;
				},
			);

			action.payload.enterprise.pendingRequests.forEach(
				(pendingRequest: Requests) => {
					newPendingRequests[pendingRequest.id] = pendingRequest;
				},
			);
			action.payload.employee.myrequests.forEach((request: Requests) => {
				newMyRequests[request.id] = request;
			});
			state.enterprise.pendingRequests = newPendingRequests;
			state.enterprise.usersandworkers = newRequests;
			state.employee.myrequests = newMyRequests;

			state.status = 'succeeded';
		},
		userFailure(state, action) {
			const { error } = action.payload;
			state.status = 'failed';
			state.error = error;
		},
		userOptionTabs(state, action: PayloadAction<string>) {
			const option = action.payload;
			state.optionTabs = option;
		},
		userOpenMenu(state, action: PayloadAction<boolean>) {
			state.isMenuOpen = action.payload;
		},
		getEmpresaActual(state, action: PayloadAction<EmpresaActualInterface>) {
			state.empresaActual = action.payload;
		},
		getLoadingInfo(state, action: PayloadAction<any>) {
			state.loadingData = action.payload;
		},
		getSwitch(state, action: PayloadAction<string>) {
			state.switch = action.payload;
		},
		getUserInfo(state, action: PayloadAction<UserInterface>) {
			state.user = action.payload;
		},
		getProfileInfo(state, action: PayloadAction<string>) {
			state.profile = action.payload;
		},
	},
});

export const {
	userLoading,
	userLoaded,
	userFailure,
	userOptionTabs,
	userOpenMenu,
	getEmpresaActual,
	getLoadingInfo,
	getSwitch,
	getUserInfo,
	getProfileInfo,
} = userSlice.actions;

export const useUserSelector = () =>
	useSelector<AppState, UserState>(({ [userSlice.name]: slice }) => slice);

export const fetchUsers = (type: string | '') => {
	return async (dispatch: Dispatch) => {
		dispatch(userLoading());
		try {
			const response = await UserService(type);
			dispatch(userLoaded(response.data));
		} catch (error) {
			dispatch(userFailure(error));
		}
	};
};

export const selectAllUsers = ({
	[userSlice.name]: { enterprise },
}: AppState): Requests[] => enterprise.usersandworkers;

export const selectAllPendingRequests = ({
	[userSlice.name]: { enterprise },
}: AppState): Requests[] => enterprise.pendingRequests;

export const selectAllMyRequests = ({
	[userSlice.name]: { employee },
}: AppState): Requests[] => employee.myrequests;

export const selectUsers = createSelector(selectAllUsers, (user) =>
	Object.values(user),
);

export const selectPendingRequests = createSelector(
	selectAllPendingRequests,
	(request) => Object.values(request),
);

export const selectMyRequests = createSelector(
	selectAllMyRequests,
	(myrequest) => Object.values(myrequest),
);

export const selectUserById = (state: AppState, userId: number): Requests => {
	return selectAllUsers(state)[userId];
};

export const selectPendingRequestById = (
	state: AppState,
	pendingRequestId: number,
): Requests => {
	return selectAllPendingRequests(state)[pendingRequestId];
};

export const selectMyRequestsById = (
	state: AppState,
	myRequestId: number,
): Requests => {
	return selectAllMyRequests(state)[myRequestId];
};

export const selectUserIds = createSelector(selectUsers, (users) =>
	users.map((user: Requests) => user.id),
);

export const selectPendingRequestIds = createSelector(
	selectPendingRequests,
	(requests) => requests.map((request: Requests) => request.id),
);

export const selectMyRequestsIds = createSelector(
	selectMyRequests,
	(myrequests) => myrequests.map((myrequest: Requests) => myrequest.id),
);

export const selectEmpresaActual = (state: AppState): string =>
	state.user.empresaActual.rutEmpresa;

export const EmpresaActual = createSelector(
	selectEmpresaActual,
	(response) => response,
);

export default userSlice.reducer;
